// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/create.js (origin/main-dev, 466 lines).
 * Text fields use contactFieldRules (same rules as CSV upload), primary-number
 * checkbox mutex with alternate numbers, up to 2 alternates, primary-number
 * derived from checkbox OR first alternate marked primary. Contact group
 * id derived from useParams.
 */
import React, { useState, useEffect } from 'react';
import { CustomFieldsSection } from './custom-fields/CustomFieldsSection';
import { useCustomFieldTemplates } from './custom-fields/useCustomFieldTemplates';
import { useContactCustomFieldsForm } from './custom-fields/useContactCustomFieldsForm';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

/* Micro-shims for react-bootstrap Form.* / Row / Col / Container — render
 * form-control markup so the layout keeps working via bootstrap CSS while
 * the file no longer imports from the compat shim. */
const cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ className, children, ...props }: any) => (
  <div className={cx('container-fluid', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBERS_CREATE,
  API_CONTACT_GROUP_MEMBERS_LIST,
} from '@/service/url';
import { contactFieldError, duplicateUpdatedToast } from '@/utilities/contactFieldRules';

const CreateContact = () => {
  const noSpecialCharRegex = /^[^\x00-\x1F\x7F]*$/; // any character except control characters (client requirement)
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    country: '',
    primaryNumber: '',
    isPrimaryNumber: false,
    alternateNumbers: [{ number: '', isPrimary: false }],
    company: '',
    isPrimary: true,
  });
  const [buttonDisable, setButtonDisable] = useState(false);
  const {
    enabled: customFieldsEnabled,
    templates: customTemplates,
    loading: customTemplatesLoading,
  } = useCustomFieldTemplates();
  const customForm = useContactCustomFieldsForm(customTemplates, customTemplatesLoading);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'primaryNumber') {
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setContact({ ...contact, [name]: cleanedValue });
      return;
    }

    const fieldLabels: Record<string, string> = {
      firstName: 'First name',
      lastName: 'Last name',
      street: 'Street',
      city: 'City',
      state: 'State',
      country: 'Country',
      company: 'Company',
    };

    const fieldError = contactFieldError(name, fieldLabels[name] || 'This field', value);
    if (fieldError) {
      toast.error(fieldError);
      return;
    }

    setContact({ ...contact, [name]: value.replace(/^\s+/, '') });
  };

  const handlePrimaryNumberCheck = (e) => {
    const { checked } = e.target;
    setContact((prev) => ({
      ...prev,
      isPrimaryNumber: checked,
      alternateNumbers: prev.alternateNumbers.map((num) => ({ ...num, isPrimary: false })),
    }));
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  const handleAlternateNumberChange = (index, field, value) => {
    const newAlt = [...contact.alternateNumbers];

    if (field === 'isPrimary') {
      newAlt.forEach((num, i) => {
        num.isPrimary = i === index ? !newAlt[index].isPrimary : false;
      });
      setContact((prev) => ({
        ...prev,
        isPrimaryNumber: false,
        alternateNumbers: newAlt,
      }));
    } else {
      newAlt[index][field] = value;
      setContact((prev) => ({ ...prev, alternateNumbers: newAlt }));
    }
  };

  const addAlternateNumber = () => {
    setContact((prev) => ({
      ...prev,
      alternateNumbers: [...prev.alternateNumbers, { number: '', isPrimary: false }],
    }));
  };

  function onListSuccess(resp) {
    setContact(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const {
      firstName,
      lastName,
      primaryNumber,
      street,
      city,
      state,
      country,
      company,
      alternateNumbers = [],
    } = contact;

    const primaryAlternate = alternateNumbers.find((num) => num.isPrimary);
    const finalPrimaryNumber = primaryAlternate ? primaryAlternate.number : primaryNumber;

    const allNumbers = [
      ...alternateNumbers.filter((num) => !num.isPrimary).map((num) => num.number),
      ...(primaryAlternate ? [primaryNumber] : []),
    ].filter((num) => num && num.trim() !== '' && num !== finalPrimaryNumber);

    const [alt1_number, alt2_number] = allNumbers.slice(0, 2);

    if (!firstName.trim() || !finalPrimaryNumber.trim()) {
      toast.error('First name and primary number are required');
      return;
    }

    if (!noSpecialCharRegex.test(firstName.trim())) {
      toast.error('First name must not contain control characters');
      return;
    }
    if (city.trim() && !noSpecialCharRegex.test(city.trim())) {
      toast.error('City must not contain control characters');
      return;
    }

    const hasPrimaryAlternate = alternateNumbers.some((num) => num.isPrimary);
    if (!contact.isPrimaryNumber && !hasPrimaryAlternate) {
      toast.error('Please mark at least one phone number as primary');
      return;
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      street,
      city,
      state,
      country,
      company,
      contact_group_id: id,
      primary_number: finalPrimaryNumber,
      alt1_number: alt1_number || null,
      alt2_number: alt2_number || null,
    };

    const customError = customForm.validate();
    if (customError) {
      toast.error(customError);
      return;
    }
    Object.assign(payload, customForm.payload(false));

    setButtonDisable(true);
    postApiCall(API_CONTACT_GROUP_MEMBERS_CREATE, payload)
      .then(onCreateSuccess)
      .catch((err) => {
        onCreateError(err);
        setButtonDisable(false);
      });
  };

  function onCreateSuccess(data) {
    if (data?.updated_existing) {
      const t = duplicateUpdatedToast(data);
      toast.info(t.title, { description: t.description, duration: 8000 });
    } else toast.success('Contact Member created successfully');
    getApiCall(API_CONTACT_GROUP_MEMBERS_LIST, {}).then(onListSuccess).catch(onError);
    setTimeout(() => {
      navigate(`/contact-group/${id}`);
    }, 1000);
  }

  function onCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to create contact');
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Create Contact"
        description="Add a single contact to a contact group. If this primary number already exists, that contact is updated with the details entered here (empty fields keep their saved values)."
        actions={
          <ShadPageButton variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </ShadPageButton>
        }
      />
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="firstName">
                  <Form.Label>
                    First name<span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={contact.firstName}
                    onChange={handleInputChange}
                    placeholder="Ex. John"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="lastName">
                  <Form.Label>Last name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={contact.lastName}
                    onChange={handleInputChange}
                    placeholder="Ex. Smith"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="street">
                  <Form.Label>Street</Form.Label>
                  <Form.Control
                    type="text"
                    name="street"
                    value={contact.street}
                    onChange={handleInputChange}
                    placeholder="Ex. Ninth Street"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="city">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={contact.city}
                    onChange={handleInputChange}
                    placeholder="Ex. New York"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="state">
                  <Form.Label>State/Province</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={contact.state}
                    onChange={handleInputChange}
                    placeholder="Ex. NY"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="country">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={contact.country}
                    onChange={handleInputChange}
                    placeholder="Ex. USA"
                  />
                </Form.Group>
              </Col>

              <Col md={9}>
                <Form.Group controlId="primaryNumber">
                  <Form.Label>
                    Primary Phone Number<span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="primaryNumber"
                    value={contact.primaryNumber}
                    onChange={handleInputChange}
                    placeholder="Ex. 0123456789"
                    required
                    maxLength={10}
                    pattern="[0-9]{10}"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                      if (e.target.value.length > 10) {
                        e.target.value = e.target.value.slice(0, 10);
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    Please enter exactly 10 digits (numbers only)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  id="isPrimaryNumber"
                  label="Set as primary"
                  name="isPrimaryNumber"
                  checked={contact.isPrimaryNumber}
                  onChange={handlePrimaryNumberCheck}
                />
              </Col>
              {contact?.alternateNumbers?.map((alt, index) => (
                <React.Fragment key={index}>
                  <Col md={9}>
                    <Form.Group controlId={`alternateNumber-${index}`}>
                      <Form.Label>
                        {index === 0 ? 'Alternate Phone Number' : `Alternate Phone Number ${index + 1}`}
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        value={alt.number}
                        onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleAlternateNumberChange(index, 'number', cleanedValue);
                        }}
                        placeholder="Ex. 0123456789"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, '');
                          if (e.target.value.length > 10) {
                            e.target.value = e.target.value.slice(0, 10);
                          }
                        }}
                      />
                      <Form.Text className="text-muted">
                        {alt.number.length === 10 ? '✓ Valid 10-digit number' : 'Enter 10 digits (numbers only)'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-center gap-2 pt-4">
                    <Form.Check
                      type="checkbox"
                      label="Set as primary"
                      checked={alt.isPrimary}
                      onChange={() => handleAlternateNumberChange(index, 'isPrimary')}
                    />
                  </Col>
                </React.Fragment>
              ))}

              <Col md={12}>
                {contact?.alternateNumbers?.length < 2 && (
                  <Button variant="outline" size="sm" onClick={addAlternateNumber} className="mb-3">
                    <FiPlus className="me-1" />
                    Add phone number
                  </Button>
                )}
              </Col>

              <Col md={12}>
                <Form.Group controlId="company">
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={contact.company}
                    onChange={handleInputChange}
                    placeholder="Ex. Voxtech Co"
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mt-3">
                {customFieldsEnabled && (
                  <CustomFieldsSection {...customForm.sectionProps} disabled={buttonDisable} />
                )}
              </Col>

              <Col md={12} className="mt-4">
                <div className="d-flex justify-content-end gap-3">
                  <Button variant="outline" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={buttonDisable}>Create Contact</Button>
                </div>
              </Col>
            </Row>
          </Form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
};

export default CreateContact;
